import { GameState } from './GameState';
import { EventBus, GameEvents, emitNoticeHook } from '../core/EventBus';
import { GameConfig } from '../core/GameConfig';
import { GameData } from '../data/GameData';
import { ScriptDef, ScriptRoleDef, ScriptGenre, ScriptTheme, AttributeKey, Contract, AuditionQualified } from '../data/types';
import { Utils } from '../core/Utils';
import { revealDueNow, specialDeadlineAbs } from '../data/tables/SpecialScripts';
import { poolEntryAbsOf, POOL_MONTHS } from '../data/tables/ScheduleTable';
import { normalNpcSystem } from './NormalNpcSystem';
import { fanSystem } from './FanSystem';
import { rankSystem } from './RankSystem';
import { noticeSystem } from './NoticeSystem';
import { assistantSystem } from './AssistantSystem';
import { varietyPromoSystem } from './VarietyPromoSystem';

/** 竞角结果条目（页面 5c · 经纪人通知，V2 按角色揭晓）。
 *  ⚠️ 竞角胜率⛔ 随结果返回、也⛔ 上屏（2026-10-10 茄子令删掉 5c 列表行与角色详情两处胜率显示）：
 *  它只在 revealAuditions 内部当掷点概率用，公式见 winRateOf。 */
export interface RevealResult {
    scriptId: string;
    roleIndex: number;
    /** 竞角成功 / 失败（成功可签约，失败可带资进组） */
    success: boolean;
}

/** 播出期长度（批6，剧级）：剧覆盖位 airMonths ?? 类型固定表 AIR_MONTHS_BY_GENRE，下限 1 个月 */
export function scriptAirMonths(script: ScriptDef): number {
    return Math.max(1, script.airMonths ?? GameConfig.AIR_MONTHS_BY_GENRE[script.genre] ?? 1);
}

/** 走古装片场的题材集合：古装/宫廷/仙侠/武侠/历史（年代 Period 暂归现代片场） */
const COSTUME_THEMES: ScriptTheme[] = [
    ScriptTheme.Ancient, ScriptTheme.Court, ScriptTheme.Xianxia, ScriptTheme.MartialArts, ScriptTheme.History,
];

/**
 * 剧级拍摄期月数（2026-09-28 统一档期批）：剧覆盖位 shootMonths ??
 * ⌈最小番位角色 shootWeeks ÷ WEEKS_PER_MONTH⌉（一番不存在时取最小番位，群像剧即并列主角档），下限 1 个月。
 * ⛔ 必须进一法：档期是全剧的杀青底线，四舍五入会小于 lead 自己的戏份（电影一番 5 周→4 周档=剧未拍完先播），逻辑硬伤。
 * 全剧组（玩家合同 + NPC 在途工作）统一按此档期拍摄——同剧同开机同杀青同开播；
 * 角色级 shootWeeks 的消费点有二：①最小番位值 → 派生剧级档期；②各角色自己的值 → 该角色合同的作废期限基准。
 */
export function scriptShootMonths(script: ScriptDef): number {
    if (script.shootMonths) return Math.max(1, script.shootMonths);
    const lead = script.roles.reduce<ScriptRoleDef | undefined>(
        (best, r) => !best || (r.billing ?? 99) < (best.billing ?? 99) ? r : best,
        undefined,
    );
    return Math.max(1, Math.ceil((lead?.shootWeeks ?? GameConfig.WEEKS_PER_MONTH) / GameConfig.WEEKS_PER_MONTH));
}

/** 剧级承诺拍摄周数 = 拍摄期月数 × WEEKS_PER_MONTH（合同 remainingWeeks/shotWeeks 与 NPC 在途 shootWeeksLeft 统一快照此值） */
export function scriptShootWeeks(script: ScriptDef): number {
    return scriptShootMonths(script) * GameConfig.WEEKS_PER_MONTH;
}

/**
 * 违约线窗长（2026-10-27 滚动排期批·茄子定：一番拍摄周期 × SCHEDULE_GRACE_MULTIPLIER 进整月；
 * 2026-10-29 术语定调：**违约线就是剧杀青时间**——本窗末月=剧杀青月，⛔ 再叫「玩家档期」，
 * 与制作窗混称正是本交界带的事故源）。
 * 与制作窗（scriptShootMonths，NPC 逐周递减与名义杀青 wrapUp 的锚）分离：开机起算，剧杀青月月末
 * 还有剩余拍摄周 = 违约（checkScheduleBreach）。旧档无 shootStartAbs 的在途合同不适用（跑完旧钟）。
 */
export function scriptScheduleMonths(script: ScriptDef): number {
    const lead = script.roles.reduce<ScriptRoleDef | undefined>(
        (best, r) => !best || (r.billing ?? 99) < (best.billing ?? 99) ? r : best,
        undefined,
    );
    return Math.max(1, Math.ceil((lead?.shootWeeks ?? GameConfig.WEEKS_PER_MONTH)
        * GameConfig.SCHEDULE_GRACE_MULTIPLIER / GameConfig.WEEKS_PER_MONTH));
}

/**
 * 开播月唯一样板（2026-10-29 茄子定调「二月杀青三月开播」）：开机 + 违约线窗长 = 剧杀青次月开播。
 * 合同侧（dramaTimelineOf.premiere）与 NPC 在途 job（NormalNpcSystem 开播闸、HeatChartSystem
 * NPC 开播月门）共用本函数，⛔ 第三处手抄加法——日历规则改动只改这里。
 */
export function scriptPremiereAbsOf(shootStartAbs: number, script: ScriptDef): number {
    return shootStartAbs + scriptScheduleMonths(script);
}

/**
 * 剧本开拍月（2026-10-27 滚动排期，全库唯一推导口）：特殊剧 = 定角夜次月（2026-10-14 茄子定
 * 「以颠倒姻缘为例都是第二年一月开拍」的成文）；普通剧 = 入池月 + POOL_MONTHS（池满次月开机）；
 * 不在排期表的普通剧（新手专属剧）= fallbackAbs 次月（新手签约只等 1 个月）。
 */
/**
 * 定角夜判据（2026-10-27 滚动排期，全库一把尺）：特殊剧=定角夜字段（revealDueNow）；
 * 普通剧=**各自的池满月月末**（入池月 + POOL_MONTHS − 1）——选角期 3 个月里随时可试，
 * 结果统一压到开拍前夜揭晓（茄子定「每部剧开拍前夜统一揭晓」；2026-10-27 二次实测回归：
 * 曾误改成「当月月末即揭晓」，失败位复活/5c 月月重复弹即由此来）。
 * 5c 开页（hasPendingReveals）、揭晓名单（revealAuditions）、月末清资格（GameState.sweepClosingScripts /
 * ScriptSystem.resetForMarketRefresh）四处同一条尺，⛔ 再各自拼。
 */
export function castingNightDue(scriptId: string, absNow: number): boolean {
    const deadline = specialDeadlineAbs(scriptId);
    if (deadline != null) return revealDueNow(scriptId, absNow);
    const entry = poolEntryAbsOf(scriptId);
    return entry != null && entry + POOL_MONTHS - 1 === absNow;
}

export function shootStartAbsOf(scriptId: string, fallbackAbs: number): number {
    const deadline = specialDeadlineAbs(scriptId);
    if (deadline != null) return deadline + 1;
    const entry = poolEntryAbsOf(scriptId);
    if (entry != null) return entry + POOL_MONTHS;
    return fallbackAbs + 1;
}

/**
 * 开拍月统一出口（含新手剧特例）：新手剧=锚月**当月**直拍（2026-10-27 茄子令「一月签约一月开拍」）；
 * 其余剧=shootStartAbsOf（排期表锚 / 特殊剧定角夜次月 / fallback 次月）。
 * 合同侧（signDirectOffer）与 NPC 派角侧（fillRole）必须同走本函数，⛔ 各写一份特判让班底锚错位。
 */
export function shootStartAbsFor(scriptId: string, fallbackAbs: number): number {
    return scriptId === GameConfig.NEWBIE_SCRIPT_ID ? fallbackAbs : shootStartAbsOf(scriptId, fallbackAbs);
}

/**
 * **剧集日历**（2026-10-27 日历化批，全库唯一时点出口）。
 *
 * 一部剧的四个日子全部由「开机月 + 剧级窗口」算术得出，不依赖任何逐周递减的状态：
 *  - `shootStart`      开机月（= shootStartAbs，必填字段；老档已随 SAVE_VERSION v16 作废）
 *  - `wrapUp`          全剧杀青月 = 开机月 + 制作窗 − 1（NPC 与玩家的名义杀青，同剧同月）
 *  - `premiere`        开播月 = 剧杀青次月（scriptPremiereAbsOf）；固定日历——玩家戏份何时拍完
 *                      都不动它（2026-10-29 茄子定「不管玩家的戏份什么时候杀青，都是剧杀青时间后播出」）
 *  - `scheduleDeadline` 剧杀青月（= 玩家违约线）= 开机月 + 违约线窗长 − 1（一番周期 ×1.5 进整月）
 *
 * 为什么要一个出口：这四个日子过去在 HeatChart / Award（两处）/ advanceAiringContracts /
 * 面板层各自手抄，注释互称「同尺」而实现各写一遍——抄漏一句保护（如 signedMonth 空判）就出 NaN。
 * 现在改日历规则只改这里。
 */
export interface DramaTimeline {
    shootStart: number;
    wrapUp: number;
    premiere: number;
    scheduleDeadline: number;
}

/** 剧集日历（唯一推导口；`premiere` 需要当前月才能反推拖期，故 nowAbs 必传） */
export function dramaTimelineOf(contract: Contract, script: ScriptDef, nowAbs: number): DramaTimeline {
    const shootStart = contract.shootStartAbs;
    const wrapUp = shootStart + scriptShootMonths(script) - 1;
    const scheduleDeadline = shootStart + scriptScheduleMonths(script) - 1;
    return {
        shootStart,
        wrapUp,
        // 开播月=剧杀青次月（2026-10-27 茄子令「档期结束下一个月开播」＝2026-10-29 澄清的
        // 「二月杀青三月开播」，违约线月即剧杀青月）：全剧统一上线节奏，玩家戏份早拍完
        // （配角 airing 早翻）也不提前——杀青≠开播
        premiere: scriptPremiereAbsOf(shootStart, script),
        scheduleDeadline,
    };
}

/**
 * 剧级档期违约线的展示文案（2026-10-30：解约弹窗与决策页行程详情共用一句「最迟第x年x月拍完」）。
 * 与 `dramaTimelineOf().scheduleDeadline` 同一把尺，⛔ 调用方各自拼年月。
 */
export function scheduleDeadlineTextOf(contract: Contract, script: ScriptDef, nowAbs: number): string {
    const abs = dramaTimelineOf(contract, script, nowAbs).scheduleDeadline;
    const sy = Math.floor((abs - 1) / 12);
    return `最迟第${sy + 1}年${abs - sy * 12}月拍完`;
}

/**
 * 玩家合同所属**全剧**是否已杀青开播（2026-10-01 收口成单函数；2026-10-27 日历化改读 dramaTimelineOf）。
 * 戏份结算批后「杀青」= 排完**自己的**戏份周数，配角合同 `status` 会早在全剧杀青前翻 `airing`，
 * 所以 `status === 'airing'` 不等于「剧在播」：判据一律 = airing **且** 到开播月。
 */
export function isDramaAired(contract: Contract, script: ScriptDef | undefined, absNow: number): boolean {
    if (contract.status !== 'airing' || !script) return false;
    return absNow >= dramaTimelineOf(contract, script, absNow).premiere;
}

/**
 * 全剧真杀青月（= 实际开播月）。保留此名作为 `dramaTimelineOf().premiere` 的语义别名，
 * 供微博剧宣窗口与评选在途作品取用（两处共用这一把尺）。
 * 返回 null = 剧本缺失（调用方沿用各自旧判据）。
 */
export function dramaPremiereAbsOf(
    contract: Contract, script: ScriptDef | undefined, nowAbs: number,
): number | null {
    if (!script) return null;
    return dramaTimelineOf(contract, script, nowAbs).premiere;
}

/**
 * 剧宣窗口（2026-10-24 用户令：读数显示播出期、可宣次数不随自己的杀青早晚变、开播月可宣；
 * 2026-10-25 修实际开播月：自己的戏份拖到门控月之后才杀青时，窗口必须跟着全剧顺延）。
 * 可宣月 = 开播月（名气首滴月）起播期 airMonths 个月。
 * 起点 = 全剧真杀青月（`dramaPremiereAbsOf`：名义门控月与实际开播月的较晚者），
 * 否则拖期杀青的播出期整段落在窗口外（2026-10-25 用户实测「播出期微博发不了剧宣」）。
 * 开播月这一发走独立文案池（DRAMA_PROMO_PREMIERE_*，见 WeiboPosts.ts）。
 * `months` = 可宣月数（含开播月）；`airMonths` = 播出期月数，只作读数分母（界面不许显示发放月数）。
 * 返回 null = signedMonth 缺省的旧档（沿用 isDramaAired 的旧判据）。
 */
export function promoWindowOf(
    contract: Contract, script: ScriptDef | undefined, nowAbs: number,
): { start: number; months: number; airMonths: number } | null {
    const start = dramaPremiereAbsOf(contract, script, nowAbs);
    if (start == null) return null;
    const airMonths = Math.max(1, contract.airMonths ?? scriptAirMonths(script));
    return { start, months: airMonths, airMonths };   // 可宣月=开播月起播出期月数（首滴=开播月，2026-10-27 新口径）
}

/**
 * 剧本系统
 * 管理剧本池刷新、试镜判定（纯门槛）、签约（合同按周递减）、通告执行
 *
 * 定稿规则：
 *  - 试镜 = 纯门槛判定：全部属性要求满足即通过，不掷骰、不耗体力（试镜本身是一周行程，耗体力在行程执行时）
 *  - 签约 = 最多 3 部通告同时，每通告一个角色；按周拍（不连续，中间可插其他行程），remainingWeeks 逐周 -1
 *  - 通告并入"工作"分类：kind='work' + contractId 关联合同
 *  - 一剧一角色：签了一个角色的剧本，其他角色不可再签
 *  - 剧本换新（refreshMarket）时，未拍完的合同作废
 */
export class ScriptSystem {
    private static instance: ScriptSystem;

    /**
     * 本月开播的在播作品（名气首滴月＝开播月），由 `advanceAiringContracts()` 每次月末结算重算。
     * ⛔ 落盘（只活一个 tick）：消费者 = `GameLoop.announceMonthlyToAssistant()` 的小助理播报，
     * 播报文本本身随 `assistantMessages` 入档 ⇒ 刷新后不会漏播也不会重播。
     * ⚠️ 2026-10-02 播报三连改判后**只带 `genre`（措辞分档用）**：`roleLabel` 随旧句「女主角今儿起和观众见面」一起退役
     * （同函数内的「播出中/播出完结」日志仍各自现算 roleLabel，与本清单无关）。
     */
    monthPremieres: { scriptId: string; workName: string; genre: ScriptGenre }[] = [];

    /**
     * 试镜失败锁定（2026-10-05 改版）：{角色key: 失败绝对周}。只锁失败发生的那个试镜周，
     * 下一个试镜周自然解锁；会话内存不落存档（读档点在月初——用户裁定）。
     */
    private auditionFailedWeek = new Map<string, number>();

    /** 绝对周序号（年*48 + 月内偏移*4 + 周），失败锁的比较基准 */
    private absoluteWeek(): number {
        const p = GameState.getInstance().player;
        return p.year * (12 * GameConfig.WEEKS_PER_MONTH) + (p.month - 1) * GameConfig.WEEKS_PER_MONTH + p.week;
    }

    /** 该角色本周是否因试镜失败被锁（同周不可重试；下个试镜周自动解锁） */
    isAuditionLocked(scriptId: string, roleIndex: number): boolean {
        const w = this.auditionFailedWeek.get(`${scriptId}_${roleIndex}`);
        return w !== undefined && w === this.absoluteWeek();
    }

    private constructor() { }

    static getInstance(): ScriptSystem {
        if (!this.instance) {
            this.instance = new ScriptSystem();
        }
        return this.instance;
    }

    /** 获取当前市场上架的剧本列表 */
    getMarketScripts(): ScriptDef[] {
        const state = GameState.getInstance();
        return state.player.marketScripts
            .map(id => GameData.SCRIPT_MAP[id])
            .filter(s => s !== undefined);
    }

    /**
     * 角色的能力 tag 缺口（特训重做批 2026-10-30）：角色带 requiredTag 而玩家未持有对应 TAG_SKILL_*
     * 时返回置灰原因文案，null=无缺口。试镜周组内层用它渲染置灰行（可点弹原因），剧本册走
     * isRoleAuditionable 硬筛不展示。
     */
    roleTagLockReason(role: ScriptRoleDef): string | null {
        if (!role.requiredTag) return null;
        if (GameState.getInstance().hasTag(role.requiredTag)) return null;
        const tagName = GameData.TAG_MAP[role.requiredTag]?.name ?? role.requiredTag;
        return `需要「${tagName}」，先去特训`;
    }

    /**
     * 角色是否可试镜（2026-10-27 经纪人工作台批自 MonthProgressPanel 迁入为公共判定）：
     * 试镜周选组层/组内角色层与经纪人页「当期剧本册」预览共用同一条筛，⛔ 两处各写一份。
     * 专属锚位（mode='exclusive'）/ 已被直签递出的位 / 已收口剧排除；等级、性别与能力 tag 门槛。
     */
    isRoleAuditionable(scriptId: string, role: ScriptRoleDef, roleIndex: number, opts?: { ignoreTagLock?: boolean }): boolean {
        if (GameData.roleAnchorOf(scriptId, role.name)?.mode === 'exclusive') return false;
        if (this.isDirectOffered(scriptId, roleIndex)) return false;
        if (this.isScriptClosedForAudition(scriptId)) return false;
        const state = GameState.getInstance();
        if (role.level > state.player.rank) return false;
        if (role.gender !== 'any' && role.gender !== state.player.gender) return false;
        if (!opts?.ignoreTagLock && this.roleTagLockReason(role)) return false;
        return true;
    }

    /** 当期市场上「有至少一个可试角色」的剧组（试镜周选组层与剧本册同源同筛） */
    auditionableScripts(): ScriptDef[] {
        return this.getMarketScripts().filter(s =>
            s.roles.some((role, i) => this.isRoleAuditionable(s.id, role, i)));
    }

    /** 获取玩家名下所有合同（含 active / done） */
    getContracts(): Contract[] {
        return GameState.getInstance().player.contracts;
    }

    /** 获取进行中的合同对应的剧本 */
    getActiveContracts(): { contract: Contract; script: ScriptDef }[] {
        const state = GameState.getInstance();
        return state.player.contracts
            .filter(c => c.status === 'active')
            .map(c => ({ contract: c, script: GameData.SCRIPT_MAP[c.scriptId] }))
            .filter(x => x.script !== undefined);
    }

    /** 当前是否可再接新通告（最多 3 个同时） */
    canContract(): boolean {
        const active = GameState.getInstance().player.contracts.filter(c => c.status === 'active').length;
        return active < GameConfig.MAX_PENDING_SCRIPTS;
    }

    /**
     * 试镜判定（纯门槛：全部属性要求满足才通过）
     * 试镜本身是一周行程，体力消耗由行程逻辑负责
     * V2 按角色（roleIndex）独立试镜
     * 2026-10-05 改版：属性不达标也允许试镜（弹窗逐项对比演出判定）；失败锁该角色到本周结束，
     * 下一个试镜周自动解锁。失败记录=会话内存（读档点在月初，不落存档——用户裁定）
     */
    audition(scriptId: string, roleIndex: number): { success: boolean; detail: string } {
        const state = GameState.getInstance();
        const script = GameData.SCRIPT_MAP[scriptId];
        if (!script) {
            return { success: false, detail: '剧本不存在' };
        }

        const role = script.roles[roleIndex];
        if (!role) {
            return { success: false, detail: '角色不存在' };
        }

        // 性别过滤：'any' 不限，否则必须匹配
        if (role.gender !== 'any' && role.gender !== state.player.gender) {
            return { success: false, detail: '角色性别不符' };
        }

        // 能力 tag 门槛（特训重做批 2026-10-30）：缺 tag = 不可试，与 isRoleAuditionable 同一条规则的两个入口。
        // 不记失败锁——没资格不算试镜失败，解锁 tag 后立即可试
        const tagLock = this.roleTagLockReason(role);
        if (tagLock) {
            return { success: false, detail: tagLock };
        }

        const missing = role.requirements.filter(req =>
            state.getAttribute(req.attr) < req.value
        );

        const success = missing.length === 0;

        if (success) {
            // 保留试镜资格（名额满时挂起，剧本换新前有效）
            const already = state.player.auditionQualified.some(
                q => q.scriptId === scriptId && q.roleIndex === roleIndex
            );
            if (!already) {
                state.player.auditionQualified.push({ scriptId, roleIndex });
            }
        } else {
            // 失败锁定：记失败绝对周，本周内不可重试该角色（同周其他角色不受影响）
            this.auditionFailedWeek.set(`${scriptId}_${roleIndex}`, this.absoluteWeek());
        }

        const roleLabel = role.name
            ? `${role.roleType}「${role.name}」`
            : role.roleType;
        const detail = success
            ? `试镜成功！全部要求达标，可签约《${script.name}》${roleLabel}`
            : `试镜失败：${missing.map(m => `${GameData.ATTRIBUTE_DEFS[m.attr].name} ${state.getAttribute(m.attr)}<${m.value}`).join('、')}`;

        EventBus.emit(GameEvents.AUDITION_RESULT, scriptId, success, 0, 0);

        return { success, detail };
    }

    /** 是否有待揭晓的竞角结果（页面 5c 触发判定用）
     *  注意：必须与 revealAuditions 同一条筛（`revealDueFor` + `isDirectlyTaken`）。旧口径只看 `auditionQualified.length`，
     *  而名单会把未到定角夜的特殊剧筛掉，于是那一夜只有特殊剧资格时判定为真、名单为空，
     *  玩家看见一张没有角色的经纪人聊天页（2026-10-14 茄子实测：第 1 年 9 月）。 */
    hasPendingReveals(): boolean {
        const state = GameState.getInstance();
        const absNow = state.player.year * 12 + state.player.month;
        return state.player.auditionQualified.some(q =>
            this.revealDueFor(q, absNow) && !this.isDirectlyTaken(q.scriptId, q.roleIndex));
    }

    /**
     * 该角色是否已被直签通道**处理完**（2026-10-27 茄子令：已确认签约/放弃的角色不再出现在 5c 页）：
     * 同剧同角有 active 合同（帮谈/递本已签）＝已处理；放弃 flag（拒绝过直签）＝已处理。
     * ⛔ pending offer 不算——挂着没点签约/放弃的，5c 页照常出（玩家还有机会走试镜侧）。
     * 开页判定（hasPendingReveals）与结果名单（revealAuditions）必须同用本判定。
     */
    private isDirectlyTaken(scriptId: string, roleIndex: number): boolean {
        const state = GameState.getInstance();
        if (state.player.flags[ScriptSystem.directDeclineKey(scriptId, roleIndex)]) return true;
        return state.player.contracts.some(c =>
            c.scriptId === scriptId && c.roleIndex === roleIndex && c.status === 'active');
    }

    /** 5c 到期判据（开页判定与结果名单共用的一把尺）：剧本还在表里 + 特殊剧已到定角夜 */
    private revealDueFor(q: AuditionQualified, absNow: number): boolean {
        return GameData.SCRIPT_MAP[q.scriptId] !== undefined && castingNightDue(q.scriptId, absNow);
    }

    // ===== 竞角揭晓（页面 5c · 经纪人通知） =====

    /**
     * 竞角胜率：保底 50%，按各要求属性超出门槛的余量累加，封顶 95%
     * 揭晓时按当时属性计算（页面 5c）
     * V2 按角色（roleIndex）计算
     */
    winRateOf(script: ScriptDef, roleIndex: number): number {
        const state = GameState.getInstance();
        const role = script.roles[roleIndex];
        if (!role) return 50;
        let bonus = 0;
        for (const req of role.requirements) {
            // 批3-A：系数随尺度反比缩放（WINRATE_BONUS_PER_POINT = 2/ATTR_SCALE），保证同超额比例→同胜率
            bonus += Math.max(0, state.getAttribute(req.attr) - req.value) * GameConfig.WINRATE_BONUS_PER_POINT;
        }
        return Math.min(95, Math.round(50 + bonus));
    }

    /**
     * 揭晓本期全部已完成试镜角色的竞角结果（每角色独立骰一次）
     * 只对 auditionQualified（试镜门槛通过=参与竞角）的角色揭晓
     * V2 逐角色揭晓
     * ⚠️ 结果落库（2026-10-10 架构审查「形状 A 反向变体」整改）：旧实现每调一次掷一次骰，
     * 而调用方是 5c 页面渲染（旧 `MonthProgressPanel.renderReveal`，该法随 10-30 废页、11-02 删除；现入口＝`MonthProgressPanel.finish` 取名单），玩家退出再进、或刷新页面重进，
     * 同一批竞角就会重摇一套新结果 ⇒ 「不满意就重来」成了免费 SL，带资进组的价码也跟着结果变。
     * 现以 `player.auditionReveal` 记忆首掷结果，本函数只读记忆、缺哪条掷哪条；
     * 记忆的作废由 clearReveal / pruneAllReveal 跟着 auditionQualified 的消失点做（不许靠本函数猜哪些是旧的）。
     */
    revealAuditions(): RevealResult[] {
        const state = GameState.getInstance();
        const absNow = state.player.year * 12 + state.player.month;
        const results: RevealResult[] = [];
        for (const q of [...state.player.auditionQualified]) {
            // 与 hasPendingReveals 同一条尺（剧本还在表 + 特殊剧已到定角夜 + 直签未处理；2026-10-14 茄子定
            // 「七月试镜也是十二月底出结果」；直签已签/已放弃剔除=2026-10-27 茄子令，见 isDirectlyTaken）。
            // 资格与首掷记忆跨季留着，见 GameState.sweepClosingScripts
            // 与 resetForMarketRefresh 的反向用法
            if (!this.revealDueFor(q, absNow)) continue;
            if (this.isDirectlyTaken(q.scriptId, q.roleIndex)) continue;
            const script = GameData.SCRIPT_MAP[q.scriptId];
            const key = ScriptSystem.revealKey(q.scriptId, q.roleIndex);
            let success = state.player.auditionReveal?.[key];
            if (success === undefined) {
                // 未掷过才掷；掷过的角色（含同一批里已揭晓、尚未处理的）一律复用，⛔ 重摇
                // 竞角必得位（`SpecialScriptDef.autoWinRoles`，2026-10-14 茄子给《颠倒姻缘》的女主＋女二点名）：
                // 试镜一过就不掷骰，直接记「竞得」。写进的还是同一份 `auditionReveal`，所以昨天收进系统层
                // 那道签约门不必改一字——凭据仍然只有一处，「必得」只是这一位不需要掷。
                success = this.isAutoWinRole(script, q.roleIndex)
                    || Math.random() < this.winRateOf(script, q.roleIndex) / 100;
                (state.player.auditionReveal ??= {})[key] = success;
            }
            results.push({ scriptId: q.scriptId, roleIndex: q.roleIndex, success });
        }
        return results;
    }

    /** 这一位在不在该剧的必得名单里（全库唯一读 `autoWinRoles` 的地方，别处再判一遍就是第二把尺）。
     *  名单里的名字在这部剧的 roles 里一个都对不上＝配错（角色改过名、写了不存在的位、或该位本就无名）：
     *  这种条目谁也保不到，静默走过等于「以为保了其实没保」，所以一部剧喊一次点名。 */
    private readonly autoWinChecked = new Set<string>();

    private isAutoWinRole(script: ScriptDef | undefined, roleIndex: number): boolean {
        if (!script) return false;
        const listed = GameData.SPECIAL_SCRIPTS.find(s => s.scriptId === script.id)?.autoWinRoles;
        if (!listed?.length) return false;
        if (!this.autoWinChecked.has(script.id)) {
            this.autoWinChecked.add(script.id);
            const dead = listed.filter(name => !script.roles.some(r => r.name === name));
            if (dead.length) {
                console.warn(`[ScriptSystem] 《${script.name}》的 autoWinRoles 写了 ${dead.join('、')}，`
                    + `但这部剧的 roles 里没有这些名字（改过角色名／写错／该位本就无名），所以这几条保不了任何人——请对 SpecialScripts.ts 与剧本表。`);
            }
        }
        return listed.includes(script.roles[roleIndex]?.name ?? '');
    }

    /** 竞角记忆键＝唯一来源 Utils.auditionRevealKey（GameState 作废时也用它，⛔ 两处各拼一遍） */
    private static revealKey(scriptId: string, roleIndex: number): string {
        return Utils.auditionRevealKey(scriptId, roleIndex);
    }

    /** 该角色的竞角流程结束（签约／带资／放弃）⇒ 记忆同步作废，日后重新试镜要重掷 */
    clearReveal(scriptId: string, roleIndex: number): void {
        const map = GameState.getInstance().player.auditionReveal;
        if (map) delete map[ScriptSystem.revealKey(scriptId, roleIndex)];
    }

    /** 竞角季整体作废（剧本换新修剪资格、收口清空、整批重排，记忆跟着资格修剪（Utils 同一把尺） */
    pruneAllReveal(): void {
        Utils.pruneAuditionReveal(GameState.getInstance().player);
    }

    /**
     * 带资进组（竞角失败角色花钱抢角色 · 页面 5c）
     * 带资价格 = 报酬 × BUYIN_MULTIPLIER；需名额未满 + 金钱足够（主动消费不允许欠债，欠债仅来自不可选择的突发惩罚）
     * 解锁条件：金钱**首次**达到 BUYIN_UNLOCK_MONEY（player.flags.BUYIN_UNLOCKED）
     * V2 按角色（roleIndex）
     */
    buyIn(scriptId: string, roleIndex: number): { ok: boolean; price: number; detail: string } {
        const state = GameState.getInstance();
        const script = GameData.SCRIPT_MAP[scriptId];
        if (!script) return { ok: false, price: 0, detail: '剧本不存在' };

        const role = script.roles[roleIndex];
        if (!role) return { ok: false, price: 0, detail: '角色不存在' };

        // 未解锁：金钱曾未达到 5 万（含富家千金开局外的普通开局）→ 锁死，即使当前余额足够
        if (!state.hasFlag(GameConfig.BUYIN_UNLOCK_FLAG)) {
            return { ok: false, price: 0, detail: '带资进组未解锁（金钱需首次达到 ¥' + Utils.formatMoney(GameConfig.BUYIN_UNLOCK_MONEY) + '）' };
        }

        // 一剧一角色：该剧本已有 active 合同则不可再签
        const active = state.player.contracts.filter(c => c.status === 'active');
        if (active.some(c => c.scriptId === scriptId)) {
            return { ok: false, price: 0, detail: '《' + script.name + '》已有签约角色在拍摄中' };
        }
        if (active.length >= GameConfig.MAX_PENDING_SCRIPTS) {
            return { ok: false, price: 0, detail: '通告已满（最多 ' + GameConfig.MAX_PENDING_SCRIPTS + ' 部）' };
        }
        const price = Math.floor(this.rewardOf(role) * GameConfig.BUYIN_MULTIPLIER);
        if (state.getMoney() < price) {
            return { ok: false, price, detail: '金钱不足（带资需 ¥' + Utils.formatMoney(price) + '）' };
        }

        state.addMoney(-price);
        // 2026-10-27 日历化批：合同只存**应拍周数**（角色级 shootWeeks），已拍进度记在
        // 合同上的 shotWeeksDone。拍摄期只增本字段，不碰应拍总量（旧 remainingWeeks 递减已删）。
        const contract: Contract = {
            id: `CT_${scriptId}_${roleIndex}_${state.player.year}_${state.player.month}_${Date.now()}`,
            scriptId,
            roleIndex,
            shotWeeks: role.shootWeeks,
            signedMonth: state.player.year * 12 + state.player.month,
            // 开拍月：与正常签约同一把尺（带资进组不改变剧组档期）
            shootStartAbs: shootStartAbsOf(scriptId, state.player.year * 12 + state.player.month),
            status: 'active',
        };
        state.player.contracts.push(contract);
        // 玩家定角 ⇒ 先回收季初 sweep 派给 NPC 的同一个位（机制与后果见 takeRoleFromNpc 注释）
        const demoted = this.takeRoleFromNpc(scriptId, roleIndex);
        // 签约时绑定同剧组 NPC：饰演其他角色的普通 NPC（已排除主角本人饰演的角色）
        this.bindContractCast(contract, scriptId, roleIndex);
        // 带资成功：该角色竞角流程结束，移出待揭晓集合
        state.player.auditionQualified = state.player.auditionQualified.filter(
            q => !(q.scriptId === scriptId && q.roleIndex === roleIndex)
        );
        this.clearReveal(scriptId, roleIndex);
        // 首次签约成就标记（成就 ACH_101「签下第一笔」月末判定）
        state.setFlag('ACH_EVENT_FIRST_CONTRACT');

        const roleLabel = role.name
            ? `${role.roleType}「${role.name}」`
            : role.roleType;
        EventBus.emit(GameEvents.SCRIPT_CONTRACTED, scriptId);
        // ⛔ 这里不再发 cast 信号：签约只代表合同成立，玩家可能把本月第一周排休息，
        // 剧情必须在**真正开工的第一周**之后演（见 shootOneWeek 的首次拍摄判定）。
        // 旧写法在签约点 emit，剧本钩子进队后会被执行页第一周（哪怕那周是休息）读掉，
        // 实测：首周休息 + 第 2~4 周拍《颠倒姻缘》⇒ 片场开拍剧情挂在休息周后面。
        return { ok: true, price, detail: '带资进组成功' };
    }

    /**
     * 竞角角色处理完成（[✍ 签约] / [放弃] / 带资）：移出待揭晓集合
     * V2 按角色（roleIndex）
     */
    settleAudition(scriptId: string, roleIndex: number): void {
        const state = GameState.getInstance();
        state.player.auditionQualified = state.player.auditionQualified.filter(
            q => !(q.scriptId === scriptId && q.roleIndex === roleIndex)
        );
        this.clearReveal(scriptId, roleIndex);   // 竞角记忆同步作废（见 revealAuditions）
        state.dropRevealRow(scriptId, roleIndex);   // 月末结转包的队列行同步摘掉（2026-11-03 竞角重排批）
    }

    /**
     * 玩家定角时把该位从 NPC 手里收回来（签约与带资共用）。
     *
     * 实现思路（2026-09-29「玩家永无提名」缺陷的修法，配套改动见下三处）：
     *  1. 派角时机是**季初换新**（`GameState.sweepUnclaimedRoles`）——那一刻玩家还没开始试镜，
     *     系统⛔ 知道谁会要哪个位，只能先把整季角色位全派出去（NPC 要与玩家同期开工，
     *     挪到玩家选完再派 = 全行业档期后移一季、评选窗口归属全变，故⛔ 挪）。
     *  2. 所以玩家后来抢到同一个位时**必须回收**：⛔ 回收就是同一个 (scriptId, roleIndex)
     *     玩家与 NPC 各演一遍、各写一条履历，而 NPC 早接角一个月（玩家侧要过「全剧杀青」门控）
     *     ⇒ 播完也早一个月 ⇒ `AwardSystem.allWorks()`「同位只留最早一次」的去重
     *     会把**玩家那条静默丢掉** ⇒ 玩家既进不了提名池也拿不到奖（分数再高也没用）。
     *  3. 回收只处理在途工作；NPC 早已播完结算的情况摘不掉历史行，由去重侧的
     *     「玩家行优先」兜底（见 AwardSystem.allWorks）⇒ 两道一起才完整。
     *  4. 反向口子：玩家事后弃演（主动解约 / 通告超期作废）时该位要重新派出去
     *     （`reclaimRole`），⛔ 留下无人饰演的空位——候选池容量由排期表唯一决定。
     *
     * @returns 被换下的 NPC 名字；该位本来就⛔ 有人时返回 null
     */
    private takeRoleFromNpc(scriptId: string, roleIndex: number): string | null {
        return normalNpcSystem.revokeRole(scriptId, roleIndex)?.name ?? null;
    }

    /**
     * 签约剧本角色（生成合同，按周拍）
     * V2 按角色（roleIndex），一剧一角色
     * 试镜门槛本身不在这查（资格早在 `audition` 那关判过，失败会锁周）；
     * 但**竞角成败在这里查**（2026-10-14 茄子令从界面层收到系统层，见下方那道门）。
     */
    contractScript(scriptId: string, roleIndex: number): boolean {
        const state = GameState.getInstance();
        const script = GameData.SCRIPT_MAP[scriptId];
        if (!script) return false;

        const role = script.roles[roleIndex];
        if (!role) return false;

        // 竞角成败校验（2026-10-14 茄子令）：这一位的竞角结果必须是「竞得」才许白签。
        // 旧形态里这条判据只活在 5c 界面的钮位分档上（旧 `MonthProgressPanel.renderReveal` 已随废页删除；当时见 success 才给「签约」钮），
        // 系统层谁都调得动，所以「点穿界面／存档被改／将来多一个签约入口」任一处失手就是没竞到却凭空签合同，
        // 还会把同剧其余位的 NPC 顶掉（`takeRoleFromNpc`）。
        // 只认 `auditionReveal` 这一处真相：它由 `revealAuditions` 首掷落库、跟着 `auditionQualified` 的消失点修剪
        // （不变式见桩 5b：记忆键集合＝在册资格集合），所以「没掷过」与「掷输了」都天然被拦，
        // 特殊剧未到定角夜也天然签不了（那之前结果还没掷）。不许再往 UI 或别处补第二道同类筛。
        // 拒签必须喊：正常链路撞不到这里，走到这＝两处判据分叉或脏档，静默 false 会让玩家以为按钮坏了。
        const rolled = state.player.auditionReveal?.[ScriptSystem.revealKey(scriptId, roleIndex)];
        if (rolled !== true) {
            console.warn(`[ScriptSystem] 拒签《${script.name}》第 ${roleIndex} 位：竞角结果不是「竞得」（库里现值=${rolled === undefined ? '未掷' : '竞角失败'}）。`
                + `5c 的「签约」钮只该出现在 success 为真的行上——请查 revealAuditions 的落库与 auditionQualified 修剪是否对齐。`);
            return false;
        }

        // 一剧一角色：该剧本已有 active 合同或待签直签 offer 则不可再签（直签 offer 同样占着「这一剧的一个位」）
        const active = state.player.contracts.filter(c => c.status === 'active');
        if (active.some(c => c.scriptId === scriptId)) {
            return false;
        }
        if (state.player.contracts.some(c => c.status === 'pending' && c.scriptId === scriptId)) {
            return false;
        }

        // 检查合同上限
        if (active.length >= GameConfig.MAX_PENDING_SCRIPTS) {
            return false;
        }

        // 2026-10-27 日历化批：只存应拍周数，进度另记 shotWeeksDone（见 Contract.shotWeeks 注释）
        const contract: Contract = {
            id: `CT_${scriptId}_${roleIndex}_${state.player.year}_${state.player.month}_${Date.now()}`,
            scriptId,
            roleIndex,
            shotWeeks: role.shootWeeks,
            signedMonth: state.player.year * 12 + state.player.month,
            // 开拍月（2026-10-27 滚动排期）：签约不改变剧组档期——普通剧=入池月+POOL_MONTHS，特殊剧=定角夜次月
            shootStartAbs: shootStartAbsOf(scriptId, state.player.year * 12 + state.player.month),
            status: 'active',
        };
        state.player.contracts.push(contract);
        // 玩家定角 ⇒ 先回收季初 sweep 派给 NPC 的同一个位（机制与后果见 takeRoleFromNpc 注释）
        const demoted = this.takeRoleFromNpc(scriptId, roleIndex);
        // 签约时绑定同剧组 NPC：饰演其他角色的普通 NPC（已排除主角本人饰演的角色）
        this.bindContractCast(contract, scriptId, roleIndex);

        // 签约成功：该角色竞角流程结束，移出待揭晓集合
        state.player.auditionQualified = state.player.auditionQualified.filter(
            q => !(q.scriptId === scriptId && q.roleIndex === roleIndex)
        );
        this.clearReveal(scriptId, roleIndex);
        // 首次签约成就标记（成就 ACH_101「签下第一笔」月末判定）
        state.setFlag('ACH_EVENT_FIRST_CONTRACT');

        const roleLabel = role.name
            ? `${role.roleType}「${role.name}」`
            : role.roleType;
        EventBus.emit(GameEvents.SCRIPT_CONTRACTED, scriptId);
        // ⛔ 签约点不发 cast（原因同带资路径）：cast 是「进组/开拍第一周」信号，见 shootOneWeek。

        return true;
    }

    // ===== 免试镜直签 · 合同生命周期（2026-10-25 新手引导批）=====
    // 职责切分（docs/新手引导-设计方案.md §二）：**合同状态变更**归本系统（生成 pending/签约/放弃/市场修剪），
    // **派发决策**（何时掷、掷中挑哪部剧哪个角色、通知怎么投）归 DirectSignSystem——那边调这边的
    // createDirectOffer 落合同，⛔ 不自己摸 contracts 数组。

    /** 直签放弃记忆 flag 键：同剧同角色不重复递（2026-10-25 拍板「放弃语义」） */
    static directDeclineKey(scriptId: string, roleIndex: number): string {
        return `DS_SKIP_${scriptId}_${roleIndex}`;
    }

    /** 该角色当前挂着待签直签 offer？（试镜池隐藏判据的唯一真相，UI 不许自己遍历 contracts） */
    isDirectOffered(scriptId: string, roleIndex: number): boolean {
        return GameState.getInstance().player.contracts.some(
            c => c.status === 'pending' && c.scriptId === scriptId && c.roleIndex === roleIndex
        );
    }

    /**
     * 该剧「选角已收口」、整剧撤出试镜剧本册？（2026-10-25 茄子令：玩家签约后该剧即刻完成选角）
     * 两种态任一成立即收口：
     *  ① 玩家在该剧有合同（active/airing/done；pending 不算＝待签期剧还在选角季，只藏那一个角色）；
     *  ② NPC 班底已进组/在播（签约时 `bindContractCast` 现场派角 ⇒ `hasActiveJob`）。
     * ② 同时兜住玩家超期作废/主动解约的尾巴：合同删了、NPC 还在拍，剧也不许回池重开选角
     * （剧组都开拍了又来试镜＝叙事崩坏）。判据唯一真相在这里，UI 侧 ⛔ 再遍历 contracts/jobs。
     */
    isScriptClosedForAudition(scriptId: string): boolean {
        const state = GameState.getInstance();
        if (state.player.contracts.some(c => c.scriptId === scriptId && c.status !== 'pending')) return true;
        return normalNpcSystem.hasActiveJob(scriptId);
    }

    /**
     * 生成一条待签直签合同（status='pending'）。调用方=DirectSignSystem（已掷中、已挑好角色）。
     * 一剧一角色口径与 contractScript 同源：该剧已有 active 合同或 pending offer ⇒ 拒（返回 null），
     * 派发侧靠前置筛通常撞不到，这里兜的是「掷中与落档之间状态刚变」的竞态。
     */
    createDirectOffer(scriptId: string, roleIndex: number): Contract | null {
        const state = GameState.getInstance();
        const script = GameData.SCRIPT_MAP[scriptId];
        const role = script?.roles[roleIndex];
        if (!script || !role) return null;
        if (state.player.contracts.some(c => c.scriptId === scriptId
            && (c.status === 'active' || c.status === 'pending'))) return null;
        const contract: Contract = {
            id: `DS_${scriptId}_${roleIndex}_${state.player.year}_${state.player.month}_${Date.now()}`,
            scriptId,
            roleIndex,
            shotWeeks: role.shootWeeks,
            signedMonth: state.player.year * 12 + state.player.month,   // pending 期＝发出月；签约时重写
            // 开拍月（2026-10-27 滚动排期）：pending 与 active 同一把尺——剧组档期不因直签改变；
            // 统一走 shootStartAbsFor（新手剧特判锚当月）：2026-10-29 补缝——首月直拍批只改了签约侧
            // 重算，pending 预填仍走 shootStartAbsOf（新手剧=次月），签前签后开机月跳变
            shootStartAbs: shootStartAbsFor(scriptId, state.player.year * 12 + state.player.month),
            status: 'pending',
        };
        state.player.contracts.push(contract);
        return contract;
    }

    /**
     * 签下直签 offer（pending → active）。决策页送达链的重档签约弹窗（ContractSignPop）调这里。
     * 与 contractScript 共用同一套合同规则（通告槽上限/一剧一角色/NPC 回收/剧组绑定），
     * 唯一差异＝免竞角校验：直签角色压根不进竞角体系（`auditionReveal` 无记录是设计态，
     * 见 §二「与试镜通道并存」），竞角门只属于试镜路径。⛔这里动任何通知/呈现（送达链归 app.ts）。
     */
    signDirectOffer(contractId: string): boolean {
        const state = GameState.getInstance();
        const contract = state.player.contracts.find(c => c.id === contractId && c.status === 'pending');
        if (!contract) return false;
        const script = GameData.SCRIPT_MAP[contract.scriptId];
        const role = script?.roles[contract.roleIndex];
        if (!script || !role) return false;

        // 通告槽上限（与 contractScript 同一把尺：active 计数，pending 不占槽）
        const active = state.player.contracts.filter(c => c.status === 'active');
        if (active.length >= GameConfig.MAX_PENDING_SCRIPTS) {
            return false;
        }

        contract.status = 'active';
        // 签约时重写签月：作废期限/剧级档期门控都从「真签约」起算（offer 可能挂了一阵才签）
        contract.signedMonth = state.player.year * 12 + state.player.month;
        // 开拍月随签约重算（2026-10-27 滚动排期）：fallback 锚「真签约」次月；新手剧=签约当月直拍
        contract.shootStartAbs = shootStartAbsFor(contract.scriptId, contract.signedMonth);
        if (contract.scriptId === GameConfig.NEWBIE_SCRIPT_ID) {
            // 首月直拍（2026-10-27 茄子令）：签约到开拍之间没有月末，换新 sweep 赶不上补派——
            // 签约即刻把非玩家位派给 NPC（ownedByPlayer 跳过玩家位；bindContractCast 随后绑定班底）
            GameState.getInstance().sweepUnclaimedRoles([GameConfig.NEWBIE_SCRIPT_ID]);
        }
        // 玩家定角 ⇒ 回收季初 sweep 派给 NPC 的同一个位（机制与后果见 takeRoleFromNpc 注释）
        const demoted = this.takeRoleFromNpc(contract.scriptId, contract.roleIndex);
        this.bindContractCast(contract, contract.scriptId, contract.roleIndex);
        state.setFlag('ACH_EVENT_FIRST_CONTRACT');

        const roleLabel = role.name ? `${role.roleType}「${role.name}」` : role.roleType;
        EventBus.emit(GameEvents.SCRIPT_CONTRACTED, contract.scriptId);
        return true;
    }

    /**
     * 放弃直签 offer（2026-10-25 拍板「放弃语义」）：当单作废（合同删除），flag 记忆同剧同角色
     * 不重复递；下月正常掷新单。不可逆——送达链的两键弹窗就是这句后果的唯一落点。
     */
    declineDirectOffer(contractId: string): boolean {
        const state = GameState.getInstance();
        const idx = state.player.contracts.findIndex(c => c.id === contractId && c.status === 'pending');
        if (idx < 0) return false;
        const contract = state.player.contracts[idx];
        state.player.contracts.splice(idx, 1);
        state.setFlag(ScriptSystem.directDeclineKey(contract.scriptId, contract.roleIndex));
        return true;
    }

    /**
     * 直签 offer 过期作废（月初 DirectSignSystem 对超期 pending 调这里）：
     * 与主动放弃的区别＝**不置 DS_SKIP flag**——玩家没主动回绝，只是没赶上，
     * 该角色日后照常可递可试。静默删合同（不播报——清理是系统卫生不是事件）。
     */
    voidDirectOffer(contractId: string): void {
        const state = GameState.getInstance();
        const idx = state.player.contracts.findIndex(c => c.id === contractId && c.status === 'pending');
        if (idx < 0) return;
        state.player.contracts.splice(idx, 1);
    }

    /** 市场换新时修剪直签 offer：剧不在新货架 ⇒ offer 作废（与 auditionQualified 修剪同一时点、同一理由——货架上的才是可递的）。新手专属剧不在货架是设计态，豁免 */
    pruneDirectOffers(market: string[]): void {
        const state = GameState.getInstance();
        state.player.contracts = state.player.contracts.filter(c =>
            !(c.status === 'pending') || market.includes(c.scriptId)
            || c.scriptId === GameConfig.NEWBIE_SCRIPT_ID
        );
    }

    /** 某份合同已拍的拍摄周数（唯一读数口；缺省=0） */
    shotWeeksDoneOf(contract: Contract): number {
        return contract.shotWeeksDone ?? 0;
    }

    /** 某份合同还差几周拍完（UI 与违约核对共用；已杀青返回 0） */
    shotWeeksLeftOf(contract: Contract): number {
        return Math.max(0, contract.shotWeeks - this.shotWeeksDoneOf(contract));
    }

    /**
     * 执行一次通告拍摄周（kind='work' + contractId 的行程）。
     * 2026-10-27 日历化批：本方法是**唯一**能推进拍摄进度的口，只改合同上的 `shotWeeksDone`；
     * 拍满即杀青转 airing。
     * 旧「轧戏并行扣周」（parallelShootWeek）随扣周模型一起删除：剧组日历由 `shootStartAbs` 定死，
     * 玩家这周拍哪部不会改变别的剧的日历，也就没有「两套钟要同步」这回事了。
     */
    shootOneWeek(contractId: string): boolean {
        const state = GameState.getInstance();
        const contract = state.player.contracts.find(c => c.id === contractId);
        if (!contract || contract.status !== 'active') return false;

        const script = GameData.SCRIPT_MAP[contract.scriptId];
        if (!script) return false;

        const done = this.shotWeeksDoneOf(contract);
        // 进组信号＝**真正开工的第一周**（还没拍过任何一周）：不能放在签约点——玩家可以把本月首周
        // 排休息、后面几周才进组，签约当天发信号会让剧本钩子（如《颠倒姻缘》片场剧情）被休息周读掉。
        if (done === 0) {
            this.emitSpecialHook('cast', script.id);
        }

        const now = done + 1;
        contract.shotWeeksDone = now;
        if (now >= contract.shotWeeks) {
            // 杀青：转入播出期（批6，片酬/属性即时结、名气滴灌、口碑播完结，见 completeShoot）
            this.completeShoot(script, contract);
            this.emitSpecialHook('done', script.id); // 特殊剧本：玩家杀青信号
        } else {
            // 通告钩子·拍摄周（2026-10-24 通告事件批）：拍摄周行程执行后、非杀青周——
            // 杀青周只发 done（completeShoot 尾巴），同周不双弹两条通告微博
            emitNoticeHook({ type: 'drama', when: 'shootWeek', refId: script.id, roleIndex: contract.roleIndex });
        }
        return true;
    }

    /**
     * 剧本热度计算（静态，纯函数）：heat = round(baseScore × 类型系数[genre] × 题材系数)
     * 题材系数 = 所有已标题材系数的算术平均值
     * 单题材时 Σ 为空 = 0，退化为主题材系数本身。
     * 详细规则见 docs/剧本/导入台账.md §1。
     */
    static computeHeat(baseScore: number, genre: ScriptGenre, themes: ScriptTheme[]): number {
        const typeFactor = GameConfig.GENRE_HEAT_FACTORS[genre];
        const themeFactor = themes
            .reduce((sum, t) => sum + GameConfig.THEME_FACTORS[t], 0) / themes.length;
        return Math.round(baseScore * typeFactor * themeFactor);
    }

    /**
     * 评分番位系数（统一表，与名气共用 GameConfig.FAME_BILLING_FACTORS）：
     * 一番 1.0 / 二番 0.7 / 三番 0.5 / 四番 0.2 / 五番 0.1；无番位（综艺）0.5；六番及以后按五番档。
     * 注意：四番及以后角色属性对评分不构成影响（computeActualScore 直接返回 baseScore），
     * 该系数在评分侧实际只对一~三番和无番位生效。
     */
    private billingFactor(billing?: number): number {
        if (billing === undefined) return GameConfig.FAME_FACTOR_NO_BILLING;
        return GameConfig.FAME_BILLING_FACTORS[Math.min(billing, 5)];
    }

    /**
     * 作品名气收益（公式与定标见 `GameConfig.FAME_INCOME_*`；2026-10-23 H 案重定标）：
     *   gain = K × (heat/100)^0.8 × 番位系数^1.2 × 咖位乘子 × 名气因子 × 软上限
     * 旧「台阶 × 份额」制作废：台阶＝门槛表怎么切，Lv.7 的台阶比 Lv.6 小 ⇒ 同一部戏顶流反而更便宜（倒挂）；
     * 现改成门槛开方的单调乘子（`RankSystem.getRankFameMult`，读**本级**门槛），结构上不可能倒挂、
     * 且尾段（Lv.7+）不再跟涨——门槛尾段只 +3000 是入场券，收入若还按「下一级」跟涨就会「一部穿一级」。
     * 顶流线锚（名气因子钳点）走 `RankSystem.getToplineFameRef()`（＝Lv.7 门槛，同源现算；⛔ 写死 24000 满级线，
     *   那是 10-22 批次漏更的病根，见 GameConfig 注释与导入台账「全库同步点清单」）；
     *   软上限另锚**满级线**（Lv.10）——两条线语义不同，混用会造出尾段倒挂（见下一段注释）。
     * 两条纪律照旧：咖位/名气取**杀青当时的**（⛔ 名气反算的等级）、热度只读**剧本底数**
     *   ⇒ 玩家砸剧宣/请客串抬起来的活热度⛔ 进这笔账（用户 2026-10-03 拍板「滴灌不跟热度」）。
     */
    fameGainOf(script: ScriptDef, role: ScriptRoleDef): number {
        const state = GameState.getInstance();
        const billingF = role.billing === undefined
            ? GameConfig.FAME_FACTOR_NO_BILLING
            : GameConfig.FAME_BILLING_FACTORS[Math.min(role.billing, 5)];
        const heatF = Math.pow(Math.max(0, script.heat) / GameConfig.FAME_HEAT_REF, GameConfig.FAME_HEAT_EXP);
        const rankMult = rankSystem.getRankFameMult(state.player.rank);
        // 名气因子：按顶流线封顶（1 + COEF 倍）；超过顶流线的名气⛔ 再放大收益
        const ref = rankSystem.getToplineFameRef();
        const fameForFactor = Math.min(state.player.fame, ref);
        const fameFactor = 1 + GameConfig.FAME_FAME_FACTOR_COEF
            * Math.log2(1 + fameForFactor / ref);
        // 名气软上限（2026-10-22 加、2026-10-23 H 案把起跳锚从顶流线回满级线）：
        // 软上限是**纯时间刹车**（防名气膨胀到亿级粉丝），不是「顶流闸门」——锚在顶流线会让玩家一过 15000
        // 就衰减，衰减盖过乘子微涨 ⇒ Lv.8 < Lv.7 倒挂（用户裁定不接受）。锚回满级线 ⇒ fame ≤ 24000 区间
        // 乘子单调，「同一部戏高咖位收益 ≥ 低咖位」这条设计约束保持有效。
        const capRef = rankSystem.getMaxFameThreshold();
        const softCap = state.player.fame > capRef
            ? Math.pow(capRef / state.player.fame, GameConfig.FAME_SOFT_CAP_EXP) : 1;
        const gain = GameConfig.FAME_INCOME_K * heatF
            * Math.pow(billingF, GameConfig.FAME_BILLING_EXP)
            * rankMult * fameFactor * softCap;
        return Math.max(1, Math.round(gain));
    }

    /**
     * 实际评分（2.1~9.9，豆瓣口径两端都不出现整数极值，9.9 即封神、2.1 即谷底）：
     * baseScore + 番位系数 × (罚项 + 奖励项)，clamp [SCORE_MIN, SCORE_MAX]
     *  - 罚项：Σ(不达标差值) / SCORE_ATTR_DIVISOR——属性不达标硬演会拉低评分（原口径不变）；
     *  - 奖励项（2026-09-29 评分通胀修·方案B）：达标后按超额比例给小额奖励，
     *    每条 SCORE_BONUS_PER_REQ × min(超额比例 / SCORE_BONUS_SAT_RATIO, 1)，合计封顶 SCORE_BONUS_TOTAL_CAP——
     *    剧本质量主导评分，属性溢出只锦上添花有限幅度，烂剧本不再能被属性硬拉上高分；
     *    旋钮含义与空跑锚点见 GameConfig.SCORE_BONUS_* 注释。
     * 四番及以后角色属性对评分不构成影响：直接返回 baseScore。
     */
    computeActualScore(script: ScriptDef, roleIndex: number): number {
        const state = GameState.getInstance();
        const role = script.roles[roleIndex];
        if (!role) return script.baseScore;
        // 四番及以后：属性项不计，评分 = 剧本基础分
        if (role.billing !== undefined && role.billing >= 4) return script.baseScore;
        const k = this.billingFactor(role.billing);
        let penalty = 0;
        let bonus = 0;
        for (const req of role.requirements) {
            const diff = state.getAttribute(req.attr) - req.value;
            if (diff < 0) {
                penalty += diff;
            } else {
                bonus += GameConfig.SCORE_BONUS_PER_REQ
                    * Math.min(diff / req.value / GameConfig.SCORE_BONUS_SAT_RATIO, 1);
            }
        }
        bonus = Math.min(bonus, GameConfig.SCORE_BONUS_TOTAL_CAP);
        const raw = script.baseScore + k * (penalty / GameConfig.SCORE_ATTR_DIVISOR + bonus);
        return Math.round(Math.max(GameConfig.SCORE_MIN, Math.min(GameConfig.SCORE_MAX, raw)) * 10) / 10;
    }

    /** 口碑影响（线性映射，6 分为界）：round((实际评分 − 6) × REPUTATION_SCORE_FACTOR) */
    computeReputationDelta(actualScore: number): number {
        return Math.round((actualScore - GameConfig.SCORE_REPUTATION_MIDLINE) * GameConfig.REPUTATION_SCORE_FACTOR);
    }

    /** 播出期长度（批6）：剧覆盖位 airMonths ?? 类型固定表，下限 1 个月（实现见 scriptAirMonths） */
    airMonthsOf(script: ScriptDef): number {
        return scriptAirMonths(script);
    }

    /** 剧级承诺拍摄周数（2026-09-28 统一档期批，全角色同值；实现见 scriptShootWeeks） */
    shootWeeksOf(script: ScriptDef): number {
        return scriptShootWeeks(script);
    }

    /** 剧级拍摄期月数（2026-09-28 统一档期批；实现见 scriptShootMonths） */
    shootMonthsOf(script: ScriptDef): number {
        return scriptShootMonths(script);
    }

    /**
     * 名气发放月数（批6 定稿修订 2026-09-26）= 播出期 + 1：
     * 播出期从杀青**次月**起算，但滴灌从**杀青当月月末**就开始（宣发预热），
     * 一直到播出期末月月末发完——1 个月播出的剧分 2 个月末发完名气。
     */
    payoutMonthsOf(script: ScriptDef): number {
        return this.airMonthsOf(script) + 1;
    }

    /**
     * 杀青（拍摄完成）结算 —— 批6 生命周期第二段入口：
     * 片酬 + 属性成长一次结（沿用旧口径）；实际评分取杀青时快照存合同；
     * 名气不再即时到账，改为**杀青当月月末起**按月滴灌至播出期末月（发完月数=播出期+1）；
     * 口碑与 filmography 在播出期末月末结算。
     * 合同转入 status='airing'（不可解约，不参与行程/签约名额）。
     */
    /** 特殊剧本生命周期信号（docs/特殊剧本实装设计.md §三）：
     *  玩家进组（首次开工周）/杀青特殊剧本即发——玩家版的剧情钩子只由玩家的合同触发。 */
    private emitSpecialHook(hook: 'cast' | 'done', scriptId: string): void {
        if (GameData.SPECIAL_SCRIPTS.some(s => s.scriptId === scriptId)) {
            EventBus.emit(hook === 'cast' ? GameEvents.SPECIAL_CAST : GameEvents.SPECIAL_DONE, scriptId);
        }
    }

    /** 特殊剧本·播出侧信号（两条**不是一个时点**，2026-10-14 茄子定）：
     *  - `airStart`（开播）发在**开播当月月末**（名气首滴那一次）——帖文写的是「开播三天/上线一周」，
     *    压到播完月（网剧＝开播后第 3 个月）就成马后炮；
     *  - `rating`（评分揭晓）发在**播出完结那个月月末**，于是落在长尾第一个月（履历行 tailK=1 那个月）演。
     *  入队后由决策页的演出档演（`FixedEventDef.playOn='decision'`），所以玩家看见的月份比 emit 晚一个月。 */
    private emitSpecialAirSignal(when: 'airStart' | 'rating', scriptId: string): void {
        if (!GameData.SPECIAL_SCRIPTS.some(s => s.scriptId === scriptId)) return;
        EventBus.emit(when === 'airStart' ? GameEvents.SPECIAL_AIR_START : GameEvents.SPECIAL_RATING, scriptId);
    }

    private completeShoot(script: ScriptDef, contract: Contract): void {
        const state = GameState.getInstance();
        const role = script.roles[contract.roleIndex];
        const roleLabel = role
            ? (role.name ? `${role.roleType}「${role.name}」` : role.roleType)
            : '';

        // 结算报酬（片酬=等级派生，特殊角色可显式 override，见 rewardOf）
        // 谈判专家（TAG_017）：片酬 +5%（2026-10-19 开局天赋拆分同批定档）
        const nego = state.hasTag('TAG_017');
        const baseReward = role ? this.rewardOf(role) : 0;
        const reward = baseReward > 0 && nego ? Math.round(baseReward * 1.05) : baseReward;
        if (reward > 0) {
            state.addMoney(reward);
        }

        // 属性成长（角色级 attributeGrowth）
        if (role && role.attributeGrowth) {
            for (const [key, value] of Object.entries(role.attributeGrowth)) {
                if (value) {
                    state.addAttribute(key as AttributeKey, value);
                }
            }
        }

        // 累计完成通告数
        state.player.totalShotsCompleted++;

        // 关联 NPC 不再在杀青时自动入页——结识一律走拍摄周偶遇 VN（见 EncounterSystem，批次 A 定稿）

        // 〔杀青追认已删（2026-10-27 滚动排期批）〕「剧组等主角」挂起随统一开拍钟废除：
        // 全剧组同月开机（shootStartAbs），玩家拖拍只拖自己的违约线（档期违约），NPC 按表走到
        // 制作窗杀青——不再存在「主角没杀青剧不能开播」的错位需要追认。

        // 进入播出期：评分/名气总额按杀青时点快照（⛔ 播完前属性再变不影响本作评分）
        // airMonthsLeft 语义=剩余发放月数（=播出期；首滴=开播月=档期结束次月，2026-10-27 新口径）
        const airMonths = this.airMonthsOf(script);
        contract.status = 'airing';
        contract.scoreSnapshot = this.computeActualScore(script, contract.roleIndex);
        contract.fameTotal = role ? this.fameGainOf(script, role) : 0;
        contract.famePaid = 0;
        contract.airMonths = airMonths;
        contract.airMonthsLeft = airMonths;   // 首滴=开播月（档期次月），发放月数=播出期（2026-10-27 新口径）

        // 通告钩子·杀青（2026-10-24 通告事件批）：所有剧本都发（特殊剧本专属的 emitSpecialHook('done')
        // 走各自口径，互不代劳）；此时月度执行页还开着，钩子命中的事件当周就演
        emitNoticeHook({ type: 'drama', when: 'done', refId: script.id, roleIndex: contract.roleIndex });

        // 宣传综艺邀约（2026-10-31 批，同日茄子令改掷骰）：一番杀青时**掷骰递约**——递约率=基础 30%
        // ×质量（baseScore 7→×1.5、8→×2）×正剧负向（×0.5），战争/谍战/历史硬闸不上；档位按题材分流
        //（粉丝向→开麦/星动，其余→大驾访谈）、定档待播宣传月（开播月）内空周；送达链走 pendingVarietyOffer
        //（app.ts deliverStep 消费）。综艺不宣综艺、非一番不触发，均在 offer 侧判定。
        if (role && role.billing === 1) {
            const premiere = dramaTimelineOf(contract, script, state.player.year * 12 + state.player.month).premiere;
            varietyPromoSystem.maybeOfferOnWrap(script, role.billing, premiere);
        }
    }

    /**
     * 月末播出结算（玩家侧，批6）：airing 合同每次 −1 发放月（首月=杀青当月月末），
     * 名气滴灌 = round(fameTotal ÷ (airMonths+1))，末月发全部余数（Σ滴灌 == fameTotal，不吞零头）；
     * 发放月数归零（=播出期末月末）→ 按杀青评分快照结算口碑、写入 filmography、转 done。
     * 播出期不占拍摄名额、不可解约。返回本月播完部数（供测试断言/播报）。
     */
    advanceAiringContracts(): number {
        const state = GameState.getInstance();
        const abs = state.player.year * 12 + state.player.month;
        let finished = 0;
        // 本月开播清单：每次月末结算重算（只活一个 tick，供 GameLoop 的小助理播报取用）
        this.monthPremieres = [];
        for (const c of state.player.contracts) {
            if (c.status !== 'airing') continue;
            const script = GameData.SCRIPT_MAP[c.scriptId];
            const role = script?.roles[c.roleIndex];
            const name = script ? `《${script.name}》` : '已下架作品';
            const roleLabel = role
                ? (role.name ? `${role.roleType}「${role.name}」` : role.roleType)
                : '';
            // 开播门控（2026-10-27 日历化批）：走 dramaTimelineOf 唯一出口。
            // 配角戏份先拍完时 airing 早于全剧杀青——未到开播月不滴灌、不倒计时（同剧同开播）。
            if (script && abs < dramaTimelineOf(c, script, abs).premiere) continue;
            const airMonths = Math.max(1, c.airMonths ?? 1);
            const payoutMonths = airMonths;   // 发放月数=播出期（首滴=开播月=档期次月，2026-10-27 新口径）
            const total = c.fameTotal ?? 0;
            const paid = c.famePaid ?? 0;
            // 「整期未滴」＝本月是它第一次滴灌的月份 ⇒ 本月开播（名气到账的那个月＝观众看到的那个月）
            const firstDrip = (c.airMonthsLeft ?? 0) >= payoutMonths;
            c.airMonthsLeft = Math.max(0, (c.airMonthsLeft ?? 0) - 1);
            if (firstDrip && !c.premiered) {
                // 「报过开播」是**事件**，落到合同上当一次性标记（2026-10-03 治本）：旧写法只信
                // `airMonthsLeft ≥ payoutMonths` 这条算术，而 `airMonths` 是可选字段——它一缺/一漂移，
                // 右边退成 2、左边还是按真实播出期写的 ⇒ 同一份合同连月各报一次「《X》开播了」
                // （用户实测「1 月底一次、2 月底又一次」，见 _test_premiere_arith.mjs 形状②）。
                c.premiered = true;
                this.monthPremieres.push({
                    scriptId: c.scriptId,
                    workName: script?.name ?? '',
                    genre: script?.genre ?? 'other',   // 措辞分档：电影讲「上映」，其余讲「开播」
                });
                // 开播时机（airStart，本分支＝开播当月月末＝播出第一个月）；评分揭晓另发在完结月，见下方
                this.emitSpecialAirSignal('airStart', c.scriptId);
            }
            // 滴灌额：非末月均摊（夹到剩余额防超发），末月（播出期末月）发全部余数
            const drip = c.airMonthsLeft <= 0
                ? Math.max(0, total - paid)
                : Math.min(total - paid, Math.round(total / payoutMonths));
            if (drip > 0) {
                state.addFame(drip);
                c.famePaid = paid + drip;
            }
            if (c.airMonthsLeft > 0) {
                continue;
            }
            // —— 播出完结：口碑按杀青评分快照落账，作品入履历 ——
            const score = c.scoreSnapshot
                ?? script?.baseScore
                ?? GameConfig.SCORE_REPUTATION_MIDLINE;
            const repDelta = this.computeReputationDelta(score);
            if (repDelta !== 0) {
                state.addAttribute('reputation', repDelta);
            }
            (state.player.filmography ??= []).push({
                scriptId: c.scriptId,
                roleIndex: c.roleIndex,
                // 展示字段落快照：剧本表日后改名/改定位不回写这条历史（缺表时沿用日志侧「已下架作品」口径）
                workName: script?.name ?? '已下架作品',
                roleType: role?.roleType ?? '',
                billing: role?.billing,
                genre: script?.genre ?? 'other',
                score,
                // 「爆」标定格（热度榜同口径：workHeat ≥ HEAT_BOOM_LINE；结算/履历展示用，
                // 剧下架后仍可显示 ⛔ 不随表现查）
                boom: (script ? fanSystem.workHeatOf(script) : 0) >= GameConfig.HEAT_BOOM_LINE,
                year: state.player.year,
                month: state.player.month,
            });
            c.status = 'done';
            finished++;
            // 评分揭晓＝长尾期第一个月（本月是播完月，履历落账后下个月 tailK=1；剧情入队即落在下个月的决策页演）
            this.emitSpecialAirSignal('rating', c.scriptId);
            // 评分口碑文案：高分封神 / 低分扑街
            const repText = repDelta === 0 ? '口碑 ±0' : `口碑 ${repDelta > 0 ? '+' : ''}${repDelta}`;
            const scoreEmoji = score >= 8 ? '🌟' : score >= 6 ? '⭐' : score >= 4 ? '💫' : '💔';
        }
        return finished;
    }

    /**
     * **月初开播信号**（2026-11-03 茄子令：播出期那条通告通用事件挪到「开播当月的月初、点确认行程与行程执行之间」）：
     * 扫玩家的 airing 合同，本月就是它的开播月，就发一条 `premiere` 通告钩子信号。
     *
     * 为什么不在月末发（旧形态）：月末 `advanceAiringContracts` 的 `firstDrip` 那支判的是「本月已滴灌」，
     * 信号那时才响，卡片要等**下一个月**的决策页才上屏＝比开播晚一个月（月末链的那些毛病都源自
     * 「结算月」和「上屏月」两套钟打架，见 §3.4bx 与批 F 的 off-by-one 教训）。这里直接在月初读
     * `dramaTimelineOf().premiere`（全库唯一开播判据出口，与月末那支同一把尺），只算一次、当月演。
     *
     * 幂等：`confirmMonth` 有 `isExecuting` 闸（一个月只走一次），载入存档停在执行中也不会重放；
     * 表侧另有月粒度去重（`PlotSystem.monthHookFired`），同月两部剧开播也只派发一条。
     */
    notifyPremiereMonth(): void {
        const state = GameState.getInstance();
        const abs = state.player.year * 12 + state.player.month;
        for (const c of state.player.contracts) {
            if (c.status !== 'airing') continue;
            const script = GameData.SCRIPT_MAP[c.scriptId];
            if (!script) continue;
            if (dramaTimelineOf(c, script, abs).premiere !== abs) continue;
            emitNoticeHook({ type: 'drama', when: 'premiere', refId: c.scriptId, roleIndex: c.roleIndex });
        }
    }

    /**
     * 月结算：档期违约检查（2026-10-27 日历化批重写）。
     * 判据一句话：**到档期末月那一天，已拍周数还不到应拍周数 ⇒ 违约**。
     * 档期 = 开机月 + scriptScheduleMonths − 1（一番周期 ×1.5 进整月）；已拍量读合同上的 `shotWeeksDone`。
     * 这是「数一遍排期」而不是「扣周」——合同在拍摄期只读，因此不存在「扣快扣慢」这类错位。
     * 能走到这里的 active 合同必然还没拍满（拍满时 shootOneWeek 已 completeShoot 转 airing）。
     * ⛔ 违约⛔ 补派 NPC：剧组已在拍（收口早已过），补派会造出第二条时钟——角色位空着，剧照拍照播。
     * pending（直签待签）不参与（offer 挂到玩家处理为止，超期作废归 rollMonthly 的 voidDirectOffer）。
     * 返回违约数（供测试断言）。
     */
    checkScheduleBreach(): number {
        const state = GameState.getInstance();
        const abs = state.player.year * 12 + state.player.month;
        const kept: Contract[] = [];
        let vetoed = 0;
        for (const c of state.player.contracts) {
            if (c.status !== 'active') { kept.push(c); continue; }
            const script = GameData.SCRIPT_MAP[c.scriptId];
            // 剧本已不存在（数据表/刷新移出）→ 直接作废（无违约金）。
            // 台词表 #4（docs/夏夏台词-文案填写表.md）：剧名从全量表回查——下架只是「不进货架」，
            // GameData.SCRIPTS 全量常量里剧名永在，⛔ 「查不到名字」不是事实
            if (!script) {
                const offShelfName = GameData.SCRIPTS.find(s => s.id === c.scriptId)?.name;
                assistantSystem.push(offShelfName
                    ? `《${offShelfName}》剧本下架了，关联的通告已作废。`
                    : `剧本下架了，关联的通告已作废。`, { kind: 'deadline' });
                continue;
            }
            const calendar = dramaTimelineOf(c, script, abs);
            if (abs < calendar.scheduleDeadline) { kept.push(c); continue; }
            // —— 到档期了还没拍满 = 违约 ——
            vetoed++;
            const penalty = this.calcPenalty(script, c.roleIndex);
            if (penalty > 0) state.addMoney(-penalty);
            // 小助理违约播报（台词表 #2/#3，2026-10-27 茄子定稿语料）：前半固定、后半按违约金有无拼
            assistantSystem.push(
                penalty > 0
                    ? `《${script.name}》的拍摄拖过了档期，剧组按合同作了违约处理，违约金${Utils.formatMoney(penalty)}已经扣了。`
                    : `《${script.name}》的拍摄拖过了档期，剧组按合同作了违约处理，好在只是小角色，剧组随时能换人，没有追究。`,
                { kind: 'deadline' });
        }
        state.player.contracts = kept;
        return vetoed;
    }

    /**
     * 角色片酬（2026-09-26 起等级派生）：显式 override 优先（同为 ×1 口径），缺省查台账 §2 基准表 PAY_BY_ROLE_LEVEL。
     * 终值 ×PAY_SCALE（2026-10-30 钱域量级旋钮，表管结构 SCALE 管量级）；等级越界按最高档兜底。
     */
    rewardOf(role: ScriptRoleDef): number {
        const base = role.reward !== undefined
            ? role.reward
            : GameConfig.PAY_BY_ROLE_LEVEL[Math.max(0, Math.min(role.level, GameConfig.PAY_BY_ROLE_LEVEL.length) - 1)] ?? 0;
        return Math.floor(base * GameConfig.PAY_SCALE);
    }

    /** 违约金 = 角色片酬 × 系数（主动解约/超期作废共用）；龙套/特约（等级 ≤ PENALTY_FREE_MAX_ROLE_LEVEL）免违约金 */
    calcPenalty(script: ScriptDef, roleIndex: number): number {
        const role = script.roles[roleIndex];
        if (!role || role.level <= GameConfig.PENALTY_FREE_MAX_ROLE_LEVEL) return 0;
        return Math.floor((this.rewardOf(role) * GameConfig.CONTRACT_PENALTY_MULTIPLIER) / 100) * 100;
    }

    /**
     * 这部剧在哪个片场拍（古装/现代）——**一把尺两处用**：签约进组的周背景（`sceneKeyForWeek`）与
     * 影视城探班垫的片场底（2026-10-12 茄子令「背景换成对应的片场背景」）。⛔ 在 UI 侧另抄一份题材集合。
     */
    isCostumeScript(scriptId: string): boolean {
        const themes = GameData.SCRIPT_MAP[scriptId]?.themes ?? [];
        return COSTUME_THEMES.some(t => themes.includes(t));
    }

    /** 剧本换新时调用：仅作废已过期的进行中合同，清空试镜资格与已展示记录 */
    resetForMarketRefresh(): void {
        const state = GameState.getInstance();
        // 只作废已过期的进行中合同（未过期的保留，继续拍摄）；done/airing 一律保留（批6）
        state.player.contracts = state.player.contracts.filter(c => {
            if (c.status === 'done' || c.status === 'airing') return true;
            const script = GameData.SCRIPT_MAP[c.scriptId];
            if (!script) return false; // 剧本已不存在 → 作废
            return true;
        });
        // 清空试镜资格（新一批剧本要重新试镜）；未到定角夜的特殊剧例外留着（「七月试镜也是十二月底
        // 出结果」2026-10-14 茄子定，与 GameState.sweepClosingScripts 同一条筛）
        const absNow = state.player.year * 12 + state.player.month;
        state.player.auditionQualified = state.player.auditionQualified.filter(
            q => !castingNightDue(q.scriptId, absNow));   // 留＝定角夜还没到的那批（同一条尺的反向）
        this.pruneAllReveal();   // 竞角记忆跟着资格走：留下的那批不许重掷，其余作废
        // 清空已展示记录（由 GameState.refreshMarket 管理，此处保持兼容）
        state.player.scriptShown = [];
    }

    /** 剧本换新（UI/事件触发） */
    refreshMarket(): void {
        GameState.getInstance().refreshMarket();
    }

    // ===== 通告解约 / 作废（页面 5b · 经纪人聊天页） =====

    /**
     * 主动解约已签约通告（仅 active 合同）
     * 违约金走 calcPenalty（报酬 × CONTRACT_PENALTY_MULTIPLIER；龙套/特约免罚），与超期作废共用
     * 返回解约结果（penalty 可能为 0 = 免罚）
     */
    breakContract(contractId: string): { ok: boolean; scriptName: string; penalty: number; detail: string } {
        const state = GameState.getInstance();
        const contract = state.player.contracts.find(c => c.id === contractId && c.status === 'active');
        if (!contract) {
            return { ok: false, scriptName: '', penalty: 0, detail: '未找到该通告合同' };
        }
        const script = GameData.SCRIPT_MAP[contract.scriptId];
        if (!script) {
            return { ok: false, scriptName: '', penalty: 0, detail: '合同关联的剧本已不存在' };
        }
        const penalty = this.calcPenalty(script, contract.roleIndex);        if (state.getMoney() < penalty) {
            const short = penalty - state.getMoney();
            return {
                ok: false, scriptName: script.name, penalty, detail: `违约金 ¥${Utils.formatMoney(penalty)} 不足`,
            };
        }

        state.addMoney(-penalty);
        state.player.contracts = state.player.contracts.filter(c => c.id !== contractId);
        // 玩家让位 ⇒ 该位重新派给 NPC（签约时已把 NPC 那份收回，⛔ 补派就留下无人饰演的空位）
        normalNpcSystem.reclaimRole(contract.scriptId, contract.roleIndex);
        return { ok: true, scriptName: script.name, penalty, detail: '已解约' };
    }

    /** 〔deadlineOf 已删（2026-10-27 滚动排期批）〕「作废期限」概念移除；展示口径改 shootStartAbs + 剧级档期，见 CharactersPanel 解约弹窗 */

    /**
     * 签约时绑定同剧组同事：先为该剧本补齐未承接的角色（一剧一角色：签约后其余角色由 NPC 承接，
     * 玩家试镜合格待签约的角色除外），再读取 activeJobs 中「饰演其他角色」的 NPC（普通 + 竞争型特殊）
     * 存到合同 castNpcIds，拍摄周偶遇池按此精确取人（不是扫全部 NPC 的在途工作）
     */
    private bindContractCast(contract: Contract, scriptId: string, roleIndex: number): void {
        const script = GameData.SCRIPT_MAP[scriptId];
        // 补齐同剧组卡司：尚未有 NPC 承接的其他角色 → 现场派角
        // （丙′ 2026-10-01：「试镜合格→保留不派」分支已删——确认即作废后合格资格不再有保留意义，
        //   保留只会让该位在收口 sweep 前悬空；玩家随后真的签下该位时 takeRoleFromNpc 回收这份绑定）
        if (script) {
            for (let i = 0; i < script.roles.length; i++) {
                if (i === roleIndex) continue; // 玩家本人饰演的角色
                // 同位去重 + 角色锚点优先 + 随机兜底，统一在 NormalNpcSystem.fillRole，⛔ 两处各写一遍
                normalNpcSystem.fillRole(script, i);
            }
        }
        // 读取同剧组同事（饰演除玩家角色外的其他角色）
        const cast: string[] = [];
        for (const npc of normalNpcSystem.getNpcs()) {
            const role = (npc.activeJobs ?? []).find(r => r.scriptId === scriptId && r.roleIndex !== roleIndex);
            if (role) cast.push(npc.id);
        }
        contract.castNpcIds = cast;
    }
}

/** 导出单例 */
export const scriptSystem = ScriptSystem.getInstance();